/* global URL */
import {
  validVersion,
  validateManifest,
  installerName,
  releaseStage,
  compareVersions,
  isForwardVersion,
} from './link-release.mjs';

// Protocol 2 understands a permanent legacy bridge plus the latest stable offer.
export const LINK_UPDATE_PROTOCOL = 2;

export function updatePayloadName(channel, version, target) {
  const name = installerName(channel, version, target);
  return target.startsWith('mac-') ? name.replace(/\.dmg$/, '.zip') : name;
}
export function updateMetadataName(channel, target) {
  return `${channel}${target.startsWith('mac-') ? '-mac' : ''}.yml`;
}
export function validateUpdateManifest(value, channel, manifestUrl) {
  const release = validateManifest(value, channel, manifestUrl);
  if (
    Object.hasOwn(release, 'latest') ||
    (Object.hasOwn(release, 'updateProtocol') && release.updateProtocol !== LINK_UPDATE_PROTOCOL)
  )
    throw new Error('Invalid immutable update manifest');
  if (!Array.isArray(release.updates) || release.updates.length !== 3)
    throw new Error('This release has no complete updater feed');
  const seen = new Set();
  for (const update of release.updates) {
    const payload = updatePayloadName(channel, release.version, update.target);
    const metadata = updateMetadataName(channel, update.target);
    const base = new URL(`${release.version}/updates/${update.target}/`, new URL('.', manifestUrl))
      .href;
    if (
      seen.has(update.target) ||
      update.url !== base ||
      update.payload !== payload ||
      !Array.isArray(update.files) ||
      update.files.length < 2 ||
      update.files.length > 3
    )
      throw new Error('Invalid updater target');
    seen.add(update.target);
    const names = new Set();
    for (const file of update.files) {
      if (
        ![payload, `${payload}.blockmap`, metadata].includes(file.name) ||
        names.has(file.name) ||
        !Number.isSafeInteger(file.size) ||
        file.size <= 0 ||
        !/^[a-f0-9]{64}$/.test(file.sha256) ||
        !/^[A-Za-z0-9+/]{86}==$/.test(file.sha512)
      )
        throw new Error('Invalid updater file');
      names.add(file.name);
    }
    if (!names.has(payload) || !names.has(metadata))
      throw new Error('Missing updater payload or metadata');
  }
  return release;
}

// The published beta.1 client ignores extra fields and reads the root beta offer.
// New clients select latest, while the root remains an enduring upgrade bridge.
export function resolveUpdateManifest(value, channel, manifestUrl) {
  if (!value || typeof value !== 'object') throw new Error('Invalid update pointer');
  const { latest, ...root } = value;
  const bridge = validateUpdateManifest(root, channel, manifestUrl);
  if (!Object.hasOwn(value, 'latest')) return bridge;
  if (
    channel !== 'beta' ||
    releaseStage(channel, bridge.version) !== 'beta' ||
    bridge.updateProtocol !== LINK_UPDATE_PROTOCOL ||
    bridge.downloads.length !== 3
  )
    throw new Error('Invalid legacy upgrade bridge');
  const release = validateUpdateManifest(latest, channel, manifestUrl);
  if (
    release.updateProtocol !== LINK_UPDATE_PROTOCOL ||
    releaseStage(channel, release.version) !== 'stable' ||
    !isForwardVersion(channel, release.version, bridge.version)
  )
    throw new Error('Invalid stable upgrade offer');
  return release;
}

export function planUpdatePointer(candidate, previous, channel, manifestUrl) {
  const release = validateUpdateManifest(candidate, channel, manifestUrl);
  if (release.updateProtocol !== LINK_UPDATE_PROTOCOL || release.downloads.length !== 3)
    throw new Error('Publication requires verified protocol-2 packages');
  const current = previous == null ? null : resolveUpdateManifest(previous, channel, manifestUrl);
  const stable = releaseStage(channel, release.version) === 'stable';
  let bridge;
  if (stable) {
    if (!previous) throw new Error('Publish a compatible bridge beta before stable');
    const root = { ...previous };
    delete root.latest;
    bridge = validateUpdateManifest(root, channel, manifestUrl);
    if (
      channel !== 'beta' ||
      releaseStage(channel, bridge.version) !== 'beta' ||
      bridge.updateProtocol !== LINK_UPDATE_PROTOCOL ||
      bridge.downloads.length !== 3 ||
      !isForwardVersion(channel, release.version, bridge.version)
    )
      throw new Error('Publish a compatible bridge beta before stable');
  }
  if (current) {
    const comparison = compareVersions(channel, release.version, current.version);
    if (comparison === 0) {
      if (
        release.commit !== current.commit ||
        release.updateProtocol !== current.updateProtocol ||
        JSON.stringify(release.downloads) !== JSON.stringify(current.downloads) ||
        JSON.stringify(release.updates) !== JSON.stringify(current.updates)
      )
        throw new Error('Current version has different contents');
      return { unchanged: true, pointer: previous };
    }
    if (!isForwardVersion(channel, release.version, current.version))
      throw new Error('Refusing downgrade or stable-to-preview promotion');
  }
  return { unchanged: false, pointer: stable ? { ...bridge, latest: release } : release };
}
export function validateUpdateInfo(info, release, update) {
  const payload = update.files.find((file) => file.name === update.payload);
  if (
    !info ||
    info.version !== release.version ||
    !validVersion(release.channel, info.version) ||
    info.packages != null ||
    !Array.isArray(info.files) ||
    info.files.length !== 1 ||
    (info.path != null && info.path !== update.payload) ||
    (info.sha512 != null && info.sha512 !== payload.sha512)
  )
    throw new Error('Invalid update metadata');
  const file = info.files[0];
  if (file.url !== update.payload || file.sha512 !== payload.sha512 || file.size !== payload.size)
    throw new Error('Updater payload does not match the verified release');
  return info;
}
